import CommonTable from 'app/components/Common/CommonTable';
import { SymbolOptionChainDataType } from 'types/ApiServicesTypes';
import {
  PositionBaseDataType,
  PositionData,
} from '../Simulator/OptionChain/types';
import { useTheme } from '@mui/material/styles';
import { memo, useContext, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { themeContext } from 'app/components/Context/ToggleThemeContext';
import { createOptionTableRowsAllGreeks } from './utils';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';

// Props are the same as OptionCustomTable

type Props = {
  optionChainData: Record<string, SymbolOptionChainDataType>;
  handleOptionClick: (
    position: PositionBaseDataType,
    contractType: string,
    strike: string,
  ) => void;
  currentStrike: number;
  positions: PositionData;
  isCE: boolean;
  date: string;
};

const OptionCustomTableAllGreeks = ({
  optionChainData,
  handleOptionClick,
  currentStrike,
  positions,
  isCE,
  date,
}: Props) => {
  const theme = useTheme();
  const tableRef = useRef<HTMLDivElement>(null);
  const {   rows, columns } = createOptionTableRowsAllGreeks(
    optionChainData,
    handleOptionClick,
    currentStrike,
    positions,
    isCE,
    date,
    theme,
  );
  const { isDarkTheme } = useContext(themeContext);

  // Calculate some statistics for the header
  const totalStrikes = Object.keys(optionChainData).length;
  const activePositions = Object.values(positions).flat().length;

  // Auto-scroll to ATM call row
  useEffect(() => {
    if (
      tableRef.current &&
      optionChainData &&
      Object.keys(optionChainData).length > 0
    ) {
      const strikePrices = Object.keys(optionChainData).sort(
        (a, b) => Number(a) - Number(b),
      );

      if (strikePrices.length < 2) return;

      // Find the strike closest to current strike (ATM)
      let atmRowIndex = -1;
      let minDiff = Infinity;

      strikePrices.forEach((strike, index) => {
        const diff = Math.abs(Number(strike) - currentStrike);
        if (diff < minDiff) {
          minDiff = diff;
          atmRowIndex = index;
        }
      });

      if (atmRowIndex !== -1) {
        const scrollToATM = () => {
          // Find the scrollable container - it's the TableContainer with class 'scrollThis'
          const tableContainer = tableRef.current?.querySelector('.scrollThis');
          if (tableContainer) {
            // Try to find the actual ATM row element
            const tableRows = tableContainer.querySelectorAll('tbody tr');
            if (tableRows.length > atmRowIndex) {
              const atmRow = tableRows[atmRowIndex];
              const rowRect = atmRow.getBoundingClientRect();
              const containerRect = tableContainer.getBoundingClientRect();

              // Calculate the center position of the row relative to the container
              const rowCenter = rowRect.top + rowRect.height / 2;
              const containerCenter =
                containerRect.top + containerRect.height / 2;
              const scrollOffset = rowCenter - containerCenter;

              tableContainer.scrollBy({
                top: scrollOffset,
                behavior: 'smooth',
              });
            } else {
              // Fallback to calculated position
              const rowHeight = 48; // Approximate row height
              const headerHeight = 56; // Approximate header height
              const atmRowPosition = atmRowIndex * rowHeight + headerHeight;
              const visibleHeight = tableContainer.clientHeight;
              const centerOffset = visibleHeight / 2;
              const scrollPosition = atmRowPosition - centerOffset;
              const finalScrollPosition = Math.max(0, scrollPosition);

              tableContainer.scrollTo({
                top: finalScrollPosition,
                behavior: 'smooth',
              });
            }
          }
        };

        // Try scrolling after different delays to ensure table is rendered
        setTimeout(scrollToATM, 100);
        setTimeout(scrollToATM, 300);
        setTimeout(scrollToATM, 500);
      }
    }
  }, [optionChainData, currentStrike]);

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      ref={tableRef}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '0 0 12px 12px',
        }}
      >
        <CommonTable
          containerStyle={{
            height: 'calc(100vh - 200px)',
            overflow: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.grey[100],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                background: theme.palette.grey[500],
              },
            },
            '&::-webkit-scrollbar-corner': {
              background: theme.palette.grey[100],
            },
            '& .MuiTable-root': {
              borderCollapse: 'separate',
              borderSpacing: 0,
              minWidth: '100%',
            },
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              '& .MuiTableRow-root': {
                backgroundColor: isDarkTheme
                  ? theme.palette.grey[50]
                  : theme.palette.grey[100],
                '& .MuiTableCell-root': {
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: theme.palette.text.primary,
                  padding: '8px 6px',
                  backgroundColor: isDarkTheme
                    ? theme.palette.grey[50]
                    : theme.palette.grey[200],
                  '&:first-of-type': {
                    borderTopLeftRadius: '8px',
                  },
                  '&:last-of-type': {
                    borderTopRightRadius: '8px',
                  },
                },
              },
            },
            '& .MuiTableBody-root': {
              '& .MuiTableRow-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
                },
                '&:nth-of-type(even)': {
                  backgroundColor: isDarkTheme
                    ? theme.palette.grey[50]
                    : theme.palette.grey[200],
                },
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  padding: '6px 6px',
                  fontSize: '0.8rem',
                  '&:first-of-type': {
                    borderLeft: '3px solid transparent',
                  },
                },
              },
              '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                paddingBottom: '16px',
              },
            },
          }}
          headerSticky={true}
          rows={rows}
          columns={columns}
          sx={{
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            },
          }}
          whichTable={'optionchainTable'}
        />
      </Paper>
    </Box>
  );
};

export default memo(OptionCustomTableAllGreeks);
